import { useEffect, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useFormik } from 'formik'
import { SelectBox, MultiSelectBox } from 'components/input_fields'
import { AssignPremiseSchema } from './ValidationSchema'
import { SaveButton } from 'components/buttons'
import { Loader } from 'components/loader'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { userRoleList } from '../../../../redux/slices/settings'
import { userList } from '../../../../redux/slices/users'
import {
    premiseList,
    addPremiseAssignment,
    updatePremiseAssignment,
} from '../../../../redux/slices/premises'
import { addUpdateHandler } from 'utils'
import { toggleBool } from '../../../../redux/slices/globalSlice'
import { useTranslation } from 'react-i18next'

const fullName = (user) => {
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
    return name || user?.loginInfo?.user_name || `User #${user?.id}`
}

export default function AssignPremiseForm({ onSuccess, editAdmin = null }) {
    const isEdit = !!editAdmin
    const [loading, setLoading] = useState(true)
    const [spinner, setSpinner] = useState(false)
    const [admins, setAdmins] = useState([])
    const [premises, setPremises] = useState([])
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const role = useSelector((state) => state?.userRole?.list)

    const role_option = role?.map((val) => ({
        value: val.id,
        label: val.role_name,
    }))

    const defaultRoleId = role?.find((val) => val.role_name === 'Xpress Admin')?.id
        || editAdmin?.userRoles?.[0]?.role_id
        || ''

    const admin_option = admins?.map((val) => ({
        value: val.id,
        label: fullName(val),
    }))

    const initialValues = {
        role_id: isEdit
            ? (editAdmin?.userRoles?.[0]?.role_id || defaultRoleId)
            : defaultRoleId,
        user_id: isEdit ? (editAdmin?.id || '') : '',
        premise_id: isEdit
            ? (editAdmin?.premiseAssignmentInfo || []).map((item) => item.premise_id)
            : [],
    }

    const { values, errors, touched, handleSubmit, setFieldValue, setFieldTouched } = useFormik({
        initialValues,
        validationSchema: AssignPremiseSchema,
        enableReinitialize: true,
        onSubmit: (formValues, action) => {
            setSpinner(true)
            const actionThunk = isEdit ? updatePremiseAssignment : addPremiseAssignment
            dispatch(actionThunk({ values: formValues })).then((res) => addUpdateHandler({
                res: res.payload,
                action,
                navigate,
                success_toast: isEdit
                    ? `Premise assignment ${t('common.success_update')}`
                    : `Premise assignment ${t('common.success_add')}`,
                wrong_toast: `${t('common.wrong')}`,
                navigate_route: '',
                spinner: setSpinner,
                toggle: () => {
                    dispatch(toggleBool())
                    onSuccess?.()
                },
            }))
        },
    })

    const assignedPremiseIds = useMemo(() => {
        const selectedAdminData = isEdit
            ? editAdmin
            : admins.find((admin) => String(admin.id) === String(values.user_id))

        return new Set(
            (selectedAdminData?.premiseAssignmentInfo || []).map((item) => item.premise_id)
        )
    }, [isEdit, editAdmin, admins, values.user_id])

    const premise_option = useMemo(() => (
        premises?.map((val) => {
            const alreadyAssigned = assignedPremiseIds.has(val.id)
            return {
                value: val.id,
                label: alreadyAssigned ? `${val.name} (Assigned)` : val.name,
                disabled: alreadyAssigned,
            }
        })
    ), [premises, assignedPremiseIds])

    const handleUserChange = (userId) => {
        setFieldValue('user_id', userId)
        setFieldValue('premise_id', [])
    }

    useEffect(() => {
        setLoading(true)
        Promise.all([
            dispatch(userRoleList({ pageable: { role_filter: 'next' }, navigate })),
            dispatch(userList({ pageable: { pageSize: 100, currentPage: 0 }, navigate })).then((res) => {
                if (res?.payload?.status === 200) {
                    setAdmins(res.payload.data.data || [])
                } else {
                    setAdmins([])
                }
            }),
            dispatch(premiseList({ pageable: { pageSize: 100, currentPage: 0 }, navigate })).then((res) => {
                if (res?.payload?.status === 200) {
                    setPremises(res.payload.data.data || [])
                } else {
                    setPremises([])
                }
            }),
        ]).finally(() => setLoading(false))
        // eslint-disable-next-line
    }, [])

    if (loading) {
        return <Loader />
    }

    return (
        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
            <div className="row">
                <div className="col-md-12">
                    <SelectBox
                        label_name={t('user.role')}
                        mandatory={true}
                        placeholder=""
                        disabled={true}
                        option={role_option}
                        name="role_id"
                        value={values.role_id}
                        onChange={(e) => setFieldValue('role_id', e)}
                        onBlur={() => setFieldTouched('role_id', true)}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        error={errors.role_id && touched.role_id ? (<span className="field_error">{errors.role_id}</span>) : null}
                    />
                </div>

                <div className="col-md-12">
                    <SelectBox
                        label_name="Xpress Admin"
                        mandatory={true}
                        placeholder=""
                        disabled={isEdit}
                        option={admin_option}
                        name="user_id"
                        value={values.user_id}
                        onChange={handleUserChange}
                        onBlur={() => setFieldTouched('user_id', true)}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        error={errors.user_id && touched.user_id ? (<span className="field_error">{errors.user_id}</span>) : null}
                    />
                </div>

                <div className="col-md-12">
                    <MultiSelectBox
                        label_name="Premises"
                        mandatory={true}
                        placeholder=""
                        disabled={!values.user_id}
                        option={premise_option}
                        name="premise_id"
                        value={values.premise_id}
                        onChange={(e) => setFieldValue('premise_id', e)}
                        onBlur={() => setFieldTouched('premise_id', true)}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        error={errors.premise_id && touched.premise_id ? (<span className="field_error">{errors.premise_id}</span>) : null}
                    />
                </div>

                <div className="col-md-12">
                    <div className="d-flex justify-content-end mt-2">
                        <div className="col-lg-4">
                            <SaveButton
                                button_name={t('common.submit')}
                                spinner={spinner}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Form>
    )
}
